/** The portal's addresses (DESIGN §16, §17.6): `/` is this week's Plan, `/w/:monday/...` the rest. */
import { isDay, mondayOf } from "./model/days.js";

export const VIEWS = ["overview", "plan", "sessions", "day", "session", "track", "athletes", "athlete", "lifts", "lift"] as const;
export type View = (typeof VIEWS)[number];

export interface PortalRoute {
  week: string;
  /** null on a page that is not one of the week's (the builder, the team). */
  view: View | null;
  id: string | null;
}

function safeDecode(s: string): string {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
}

export function weekParam(value: string | undefined, today: string): string {
  return isDay(value) ? mondayOf(value) : mondayOf(today);
}

/** The places that are about people and lifts over time, not one week: their addresses carry no week. */
export const TIMELESS: readonly View[] = ["athletes", "athlete", "lifts", "lift"];

export function parseRoute(pathname: string, today: string): PortalRoute {
  const current = mondayOf(today);
  const parts = pathname.replace(/^\/+|\/+$/g, "").split("/");
  if (parts.length === 1 && parts[0] === "") return { week: current, view: "plan", id: null };
  if ((TIMELESS as readonly string[]).includes(parts[0]!)) {
    return { week: current, view: parts[0] as View, id: parts.length > 1 ? safeDecode(parts.slice(1).join("/")) : null };
  }
  if (parts[0] !== "w") return { week: current, view: null, id: null };
  const week = weekParam(parts[1], today);
  const name = parts[2] ?? "overview";
  const view = (VIEWS as readonly string[]).includes(name) ? (name as View) : "overview";
  const id = parts.length > 3 ? safeDecode(parts.slice(3).join("/")) : null;
  return { week, view, id };
}

export function href(week: string, view: View = "overview", id?: string | null): string {
  if (TIMELESS.includes(view)) return "/" + view + (id ? "/" + encodeURIComponent(id) : "");
  if (view === "overview") return "/w/" + week;
  return "/w/" + week + "/" + view + (id ? "/" + encodeURIComponent(id) : "");
}

export function sheetURL(week: string): string {
  return "/web/api/weeks/" + week + "/document";
}
